import { useMutation, useQuery } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { QueryParams } from '@/model';
import { BannerService } from '../instances/banner';
import { BannerConverter } from '@/converters';
import { CreateBannerRequest, UpdateBannerRequest } from '../request/banner';

export const useBanner = (params?: QueryParams) => {
  const query = useQuery(
    ['GET_BANNER', params],
    async () => {
      const [err, data] = await BannerService.getBanners(params);
      if (err) {
        throw err;
      }
      return {
        data: BannerConverter.toDomainFromPaginatedResponse(data),
        meta: data.meta,
      };
    },
    {
      refetchOnWindowFocus: false,
    },
  );

  const forceRefetch = useCallback(() => {
    query.remove();
    query.refetch();
  }, [query]);

  const returnQuery = useMemo(() => ({ ...query, forceRefetch }), [forceRefetch, query]);
  return returnQuery;
};

export const useBannerById = (id: string) => {
  const query = useQuery(
    ['GET_BANNER_BY_ID', id],
    async () => {
      const [err, data] = await BannerService.getBannerById(id);
      if (err) {
        throw err;
      }
      return BannerConverter.toDomainFromByIdResponse(data);
    },
    {
      refetchOnWindowFocus: false,
      enabled: !!id,
    },
  );

  const forceRefetch = useCallback(() => {
    query.remove();
    query.refetch();
  }, [query]);

  const returnQuery = useMemo(() => ({ ...query, forceRefetch }), [forceRefetch, query]);
  return returnQuery;
};

export const usePostBannerMutation = () => {
  return useMutation({
    mutationKey: ['POST_BANNER'],
    mutationFn: async (payload: CreateBannerRequest) => {
      const [err, data] = await BannerService.postBanner(payload);
      if (err) {
        throw err;
      }
      return BannerConverter.toDomainFromPostResponse(data);
    },
  });
};

export const useUpdateBannerMutation = () => {
  return useMutation({
    mutationKey: ['UPDATE_BANNER'],
    mutationFn: async ({ id, payload }: { id: string; payload: UpdateBannerRequest }) => {
      const [err] = await BannerService.updateBanner(id, payload);
      if (err) {
        throw err;
      }
    },
  });
};

export const useDeleteBannerMutation = () => {
  return useMutation({
    mutationKey: ['DELETE_BANNER'],
    mutationFn: async (id: string) => {
      const [err] = await BannerService.deleteBanner(id);
      if (err) {
        throw err;
      }
    },
  });
};

export const useGetBannerByUUID = () => {
  return useMutation({
    mutationKey: ['GetBannerByUUID'],
    mutationFn: async (uuid: string) => {
      const [err, data] = await BannerService.getBannerById(uuid);
      if (err) {
        throw err;
      }
      return data;
    },
  });
};

export const useBannerIfEnable = (isEnable: boolean, params?: QueryParams) => {
  const query = useQuery(
    ['GET_BANNER', params],
    async () => {
      if (!isEnable) {
        return { data: [], meta: {} };
      }

      const [err, data] = await BannerService.getBanners(params);
      if (err) {
        throw err;
      }
      return {
        data: BannerConverter.toDomainFromPaginatedResponse(data),
        meta: data.meta,
      };
    },
    {
      refetchOnWindowFocus: false,
      enabled: isEnable,
      suspense: !isEnable,
    },
  );

  const forceRefetch = useCallback(() => {
    query.remove();
    query.refetch();
  }, [query]);

  const returnQuery = useMemo(() => ({ ...query, forceRefetch }), [forceRefetch, query]);
  return returnQuery;
};
